import { DataManager } from '@syncfusion/ej2-data';
import { ClickEventArgs, ItemModel } from '@syncfusion/ej2-navigations';
import {
    CheckBoxChangeEventArgs,
    ColumnChooser,
    ContextMenu,
    Edit,
    Filter,
    FilterSettingsModel,
    Freeze,
    GridActionEventArgs,
    GridComponent,
    Inject,
    KeyboardEventArgs,
    PageSettingsModel,
    QueryCellInfoEventArgs,
    Resize,
    RowDataBoundEventArgs,
    RowDeselectEventArgs,
    RowSelectEventArgs,
    SearchSettingsModel,
    SelectionSettingsModel,
    Sort,
    SortSettingsModel,
    Toolbar,
    VirtualScroll,
} from '@syncfusion/ej2-react-grids';
import { RefObject, useEffect, useRef } from 'react';
import { StyledTable } from '../StyledTable';
import { observeFilterMenuTestAttributes } from './filterMenuTestAttributes';
import { REPORTS_GRID_ID } from './reportsGridDialogStyles';
import { IReportsTableColumnsProps, IReportsTableTemplates, renderReportsTableColumns } from './ReportsTableColumns';

const reportNameColumnIndex = '2';

export interface IReportsBrowseGridProps {
    gridRef: RefObject<GridComponent | null>;
    dataManager: DataManager;
    templates: IReportsTableTemplates;
    columnsProps: Omit<IReportsTableColumnsProps, 'templates' | 'mode'>;
    tableKey: string;
    height: string;
    toolbarItems: (string | ItemModel)[];
    onToolbarClick: (args: ClickEventArgs) => void;
    sortSettings: SortSettingsModel | undefined;
    pageSettings: PageSettingsModel;
    searchSettings: SearchSettingsModel;
    filterSettings: FilterSettingsModel;
    columnChooserSettings: { operator: string };
    selectionSettings: SelectionSettingsModel;
    onCreated: () => void;
    onDataBound: () => void;
    onActionBegin: (args: GridActionEventArgs) => void;
    onActionComplete: (args: GridActionEventArgs) => void;
    onQueryCellInfo: (args: QueryCellInfoEventArgs) => void;
    onRowDataBound: (args: RowDataBoundEventArgs) => void;
    onRowSelected: (args: RowSelectEventArgs) => void;
    onRowDeselected: (args: RowDeselectEventArgs) => void;
    onCheckBoxChange: (args: CheckBoxChangeEventArgs) => void;
    onBeforeOpenColumnChooser: () => void;
}

const ReportsBrowseGrid = ({
    gridRef,
    dataManager,
    templates,
    columnsProps,
    tableKey,
    height,
    toolbarItems,
    onToolbarClick,
    sortSettings,
    pageSettings,
    searchSettings,
    filterSettings,
    columnChooserSettings,
    selectionSettings,
    onCreated,
    onDataBound,
    onActionBegin,
    onActionComplete,
    onQueryCellInfo,
    onRowDataBound,
    onRowSelected,
    onRowDeselected,
    onCheckBoxChange,
    onBeforeOpenColumnChooser,
}: IReportsBrowseGridProps) => {
    const checkboxCellClickCleanupRef = useRef<(() => void) | null>(null);
    const filterMenuObserverCleanupRef = useRef<(() => void) | null>(null);

    useEffect(
        () => () => {
            checkboxCellClickCleanupRef.current?.();
            checkboxCellClickCleanupRef.current = null;
            filterMenuObserverCleanupRef.current?.();
            filterMenuObserverCleanupRef.current = null;
        },
        []
    );

    const clickOnChildLink = (targetElement: HTMLElement) => {
        const isReportNameColumn = targetElement?.getAttribute('data-colindex') === reportNameColumnIndex;
        const isLinkElement = targetElement?.role === 'link';

        if (isReportNameColumn) {
            targetElement.querySelector('a')?.click();
            return;
        }

        if (isLinkElement) {
            targetElement.click();
        }
    };

    const gridKeyPressed = (args: KeyboardEventArgs) => {
        if (gridRef.current) {
            gridRef.current.focusModule.content.target = undefined;
        }

        const targetElement = args.target as HTMLElement;

        if (args.code === 'Enter' || args.code === 'Space') {
            if (targetElement && !targetElement.classList.contains('e-headercell')) {
                args.cancel = true;
                // Space on the focused checkbox input already toggles it natively (on keyup); clicking the frame too would toggle twice.
                if (args.code === 'Space' && targetElement.matches('input[type="checkbox"]')) {
                    return;
                }
                const checkboxFrame = targetElement.closest('.e-rowcell.e-gridchkbox')?.querySelector<HTMLElement>('.e-frame');
                if (checkboxFrame) {
                    checkboxFrame.click();
                    return;
                }
                clickOnChildLink(targetElement);
            }
        }

        if (args.code === 'ArrowRight') {
            let cellElement = targetElement;

            if (targetElement.tagName !== 'TD' && targetElement.tagName !== 'BUTTON') {
                cellElement = targetElement.closest('td') as HTMLElement;
            }

            if (cellElement) {
                const childButton = cellElement.querySelector('button');

                if (childButton) {
                    args.cancel = true;
                    args.preventDefault();
                    childButton.focus();
                }
            }
        }
    };

    const handleCreated = () => {
        onCreated();

        filterMenuObserverCleanupRef.current?.();
        filterMenuObserverCleanupRef.current = observeFilterMenuTestAttributes(gridRef.current);

        checkboxCellClickCleanupRef.current?.();
        const gridElement = gridRef.current?.element;
        if (!gridElement) {
            return;
        }
        const forwardCheckboxCellClick = (event: Event) => {
            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }
            if (target.closest('.e-checkbox-wrapper')) {
                return;
            }

            const rowCheckboxCell = target.closest('.e-rowcell.e-gridchkbox');
            if (rowCheckboxCell) {
                rowCheckboxCell.querySelector<HTMLElement>('.e-frame')?.click();
                return;
            }

            const headerCheckboxCell = target.closest('th.e-headercell');
            if (headerCheckboxCell?.querySelector('.e-headerchkcelldiv')) {
                headerCheckboxCell.querySelector<HTMLElement>('.e-frame')?.click();
            }
        };
        gridElement.addEventListener('click', forwardCheckboxCellClick);
        checkboxCellClickCleanupRef.current = () => {
            gridElement.removeEventListener('click', forwardCheckboxCellClick);
        };
    };

    return (
        <StyledTable
            id={REPORTS_GRID_ID}
            created={handleCreated}
            dataSource={dataManager}
            cssClass=''
            allowResizing
            allowSorting
            allowFiltering
            showColumnChooser
            columnChooserSettings={columnChooserSettings}
            sortSettings={sortSettings}
            pageSettings={pageSettings}
            enableVirtualization
            enableVirtualMaskRow
            height={height}
            ref={gridRef}
            keyPressed={gridKeyPressed}
            dataBound={onDataBound}
            selectionSettings={selectionSettings}
            queryCellInfo={onQueryCellInfo}
            rowDataBound={onRowDataBound}
            rowHeight={36}
            actionComplete={onActionComplete}
            actionBegin={onActionBegin}
            searchSettings={searchSettings}
            filterSettings={filterSettings}
            toolbar={toolbarItems}
            toolbarClick={onToolbarClick}
            beforeOpenColumnChooser={onBeforeOpenColumnChooser}
            key={tableKey}
            rowSelected={onRowSelected}
            rowDeselected={onRowDeselected}
            checkBoxChange={onCheckBoxChange}
            loadingIndicator={{ indicatorType: 'Shimmer' }}
            editSettings={{ allowDeleting: true }}
        >
            {renderReportsTableColumns({ templates, ...columnsProps, mode: 'browse' })}
            <Inject services={[Sort, VirtualScroll, Resize, ContextMenu, Edit, Toolbar, Filter, ColumnChooser, Freeze]} />
        </StyledTable>
    );
};

export default ReportsBrowseGrid;
